import React from 'react';

/**
 * Delivery state of an outgoing message, drawn rather than switched on.
 *
 * Three states, which is all the data actually supports:
 *
 *   pending — handed to the socket, no ack back yet (a clock)
 *   sent    — the server acknowledged it (one tick)
 *   read    — the recipient's read receipt covers this message (two, in accent)
 *
 * There is deliberately no "delivered". Nothing in the schema records a message
 * reaching a device — `chats.status` is a boolean and `message:read` is the only
 * receipt event — so a third state would be inventing information the server
 * never sent. If a per-device receipt is added later, this is the one place that
 * has to change.
 *
 * `--tick-len` is the path length the draw-on animation retracts; it lives on the
 * element because the two shapes have different lengths.
 */
const MessageTicks = ({ state = 'sent' }) => {
  if (state === 'failed') {
    return (
      <svg
        className="msg-tick is-failed"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        aria-label="Not sent"
        role="img"
      >
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 8v4.5" />
        <path d="M12 16h.01" />
      </svg>
    );
  }

  if (state === 'pending') {
    return (
      <svg
        className="msg-tick is-pending"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        aria-label="Sending"
        role="img"
        style={{ '--tick-len': 30 }}
      >
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.8V12l3 2" />
      </svg>
    );
  }

  const read = state === 'read';

  return (
    <svg
      className={`msg-tick${read ? ' is-read' : ''}`}
      width={read ? 18 : 13}
      height="13"
      viewBox={read ? '0 0 24 17' : '0 0 17 17'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label={read ? 'Read' : 'Sent'}
      role="img"
      style={{ '--tick-len': 18 }}
    >
      <path d="m1.5 9.5 4 4 8-11" />
      {read && <path d="m9.5 9.5 4 4 8-11" />}
    </svg>
  );
};

export default MessageTicks;
